<template>
  <div class="export-task-item d-flex align-items-center justify-content-between gap-3 py-2">
    <div class="flex-grow-1 overflow-hidden">
      <div class="d-flex align-items-center gap-2 mb-1">
        <span class="text-truncate">{{ sysnameLabel }}</span>
        <span class="badge" :class="statusBadgeClass">{{ statusLabel }}</span>
      </div>

      <div v-if="task.status === ExportTaskStatus.IN_PROGRESS" class="mb-1">
        <b-progress :max="task.progress.of" style="height: 6px">
          <b-progress-bar :value="task.progress.sheet" class="bg-success"></b-progress-bar>
        </b-progress>
        <small class="text-muted">{{ progressLabel }}</small>
      </div>

      <div v-else-if="task.status === ExportTaskStatus.FAILED" class="small text-danger text-truncate">
        {{ helper.trans('export_tasks.generation_error') }}
      </div>

      <small class="text-muted d-block">{{ dateHelper.format(task.createdAt) }}</small>
    </div>

    <div class="d-flex gap-2 flex-shrink-0">
      <button v-if="task.downloadable" type="button" class="btn btn-sm btn-outline-success text-nowrap"
              :disabled="downloading" @click.prevent="onDownload">
        <i class="fa fa-download"></i> {{ helper.trans('button.download') }}
      </button>
      <button v-if="task.status === ExportTaskStatus.IN_PROGRESS" type="button" class="btn btn-sm btn-outline-danger text-nowrap"
              :disabled="cancelling" @click.prevent="onCancel">
        {{ helper.trans('button.cancel') }}
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import {computed, defineComponent, PropType, ref} from 'vue'
import {useStore} from 'vuex';
import {useNotification} from "@kyvg/vue3-notification";
import axios from "axios";
import {helper} from "@/service/CommonHelper";
import {dateHelper} from "@/service/DateHelper";
import {api} from "@/service/APIClient";
import ExportTaskResponseDTO from "@/models/ExportTaskResponseDTO";
import {ExportTaskStatus} from "@/models/enum/ExportTaskStatus";

export default defineComponent({
  props: {
    task: {
      type: Object as PropType<ExportTaskResponseDTO>,
      required: true,
    },
  },
  setup(props) {
    const store = useStore();
    const notification = useNotification();
    const downloading = ref(false);
    const cancelling = ref(false);

    const sysnameLabel = computed(() => {
      return helper.trans(`export_tasks.sysname.${props.task.sysname}`, {}, props.task.sysname);
    });

    const statusLabel = computed(() => helper.trans(`export_tasks.status.${props.task.status}`));

    const statusBadgeClass = computed(() => {
      switch (props.task.status) {
        case ExportTaskStatus.COMPLETED:
          return 'bg-success';
        case ExportTaskStatus.FAILED:
          return 'bg-danger';
        case ExportTaskStatus.CANCELLED:
          return 'bg-secondary';
        default:
          return 'bg-info';
      }
    });

    const progressLabel = computed(() => {
      return helper.trans('export_tasks.progress', {
        sheet: props.task.progress?.sheet ?? 0,
        of: props.task.progress?.of ?? 0,
        rows: props.task.progress?.rows ?? 0,
      });
    });

    const onDownload = async () => {
      downloading.value = true;
      try {
        const blob = await api.call.exportTasks.download(props.task.id, props.task.filename ?? undefined);
        const generatedAt = dateHelper.format(props.task.completedAt ?? props.task.createdAt, 'YYYY-MM-DD_HH-mm-ss');
        const filename = `${props.task.sysname}_${generatedAt}.xlsx`;
        helper.downloadBlob(blob, filename);
      } catch (e) {
        if (axios.isAxiosError(e) && e.response?.status === 404) {
          notification.notify({type: 'warn', title: helper.trans('export_tasks.download_expired')});
        } else {
          notification.notify({type: 'error', title: helper.trans('labels.error')});
        }
      } finally {
        downloading.value = false;
      }
    };

    const onCancel = async () => {
      cancelling.value = true;
      try {
        await store.dispatch('exportTasks/cancel', props.task.id);
      } catch (e) {
        notification.notify({type: 'error', title: helper.trans('labels.error')});
      } finally {
        cancelling.value = false;
      }
    };

    return {
      helper,
      dateHelper,
      ExportTaskStatus,
      sysnameLabel,
      statusLabel,
      statusBadgeClass,
      progressLabel,
      downloading,
      cancelling,
      onDownload,
      onCancel,
    }
  }
})
</script>
